import { flatMap, difference, uniq, drop, findIndex, fromPairs, map, omit, sortBy, take, compact } from 'lodash';

import { BalanceState } from 'app/store/modules/balance/reducer';
import { Balance, CoinHoldings } from 'app/types/api';
import { AddressInfo } from 'app/types/ethplorer';
import { CoinHistory, HistoricalPoint, PriceSymbol, CoinPrices } from 'app/types/interface';
import { marketChartRangeToInterface } from 'app/types/coingecko';
import { services } from 'app/services';
import { State } from 'app/store/types';
import { TimeRange } from 'app/types/interface';
import { chunkDelay } from 'app/utils';
import { loadingActionBuilder } from 'app/store/modules/utils';

export type LoadingKey = 'ethereumBalances' | 'coinPrices' | 'balances' | 'coinHistorical' | 'allCoinsHistorical';

export type CoinOrigin = 'cryptoCompare' | 'coingecko';

export type CoinPricesAction = { type: 'balance/COIN_PRICES'; payload: CoinPrices };
export type BalancesAction = { type: 'balance/BALANCES'; payload: Balance | Array<Balance> };
export type CoinHistorical = { type: 'balance/COIN_HISTORICAL'; payload: Array<CoinHistory> };
export type DisableCoinsAction = { type: 'balance/DISABLE_COINS'; payload: string | Array<string> };
export type SetCoinOrigins = { type: 'balance/SET_COIN_ORIGINS'; payload: Array<[coin: string, origin: CoinOrigin]> };
export type CalculateCoinHoldingsAction = { type: 'balance/CALCULATE_COIN_HOLDINGS' };
export type TrackCoinsAction = { type: 'balance/TRACK_COINS'; payload: Array<string> };
export type EthereumBalancesAction = { type: 'balance/ETHEREUM_BALANCES'; payload: AddressInfo | Array<AddressInfo> };
export type LoadingCoinsAction = { type: 'balance/LOADING_COINS'; payload: BalanceState['loadingCoins'] };
export type LoadingAction = { type: 'balance/LOADING'; payload: { key: LoadingKey; isLoading: boolean } };

export type BalanceActions =
  | CoinPricesAction
  | BalancesAction
  | CoinHistorical
  | DisableCoinsAction
  | SetCoinOrigins
  | CalculateCoinHoldingsAction
  | TrackCoinsAction
  | EthereumBalancesAction
  | LoadingCoinsAction
  | LoadingAction;

export const loadingAction = loadingActionBuilder<'balance/LOADING', LoadingKey>('balance/LOADING');

export const disableCoinsAction = (coinOrCoins: string | Array<string>): DisableCoinsAction => ({
  type: 'balance/DISABLE_COINS',
  payload: coinOrCoins,
});

export const balancesAction = (balanceOrBalances: Balance | Array<Balance>): BalancesAction => ({
  type: 'balance/BALANCES',
  payload: balanceOrBalances,
});

export const ethereumBalancesAction = (
  balanceOrBalances: AddressInfo | Array<AddressInfo>
): EthereumBalancesAction => ({
  type: 'balance/ETHEREUM_BALANCES',
  payload: balanceOrBalances,
});

export const calculateCoinHoldingsAction = (): CalculateCoinHoldingsAction => ({
  type: 'balance/CALCULATE_COIN_HOLDINGS',
});

export const fetchCoinPrices = (coins?: Array<string>) => async (dispatch: Dispatch, getState: () => State) => {
  dispatch(loadingAction('coinPrices', { isLoading: true }));

  // the loading flag has to come back down even when every provider fails, otherwise
  // the dashboard sits on a spinner forever
  try {
    const {
      balance: { trackedCoins, spamCoins },
    } = getState();

    // Every erc-20 token ever airdropped to a watched address lands in trackedCoins,
    // which is what pushes a handful of real holdings past coingecko's 50-symbol cap.
    // Spam tokens have no price worth asking for, so leave them out of the lookup and
    // disable them directly.
    const coinsToLookup = coins && coins.length ? coins : trackedCoins;
    const spam = coinsToLookup.filter(coin => coin in spamCoins);
    const coinsWithBTC = uniq(difference(coinsToLookup, spam).concat('BTC'));

    let result: CoinPrices | undefined;
    const primaryOrigin: CoinOrigin = 'coingecko';

    try {
      result = await services.coinGeckoService.getPricesBySymbol(coinsWithBTC);
    } catch (e) {
      console.error('coingecko prices failed', e);
    }

    if (!result || Object.keys(result).length === 0) {
      throw new Error('could not load coin prices from any provider');
    }

    const coinsWithPrices = Object.keys(result);
    dispatch({ type: 'balance/SET_COIN_ORIGINS', payload: coinsWithPrices.map(coin => [coin, primaryOrigin]) });

    const coinsWithoutPrices = difference(coinsWithBTC, coinsWithPrices).concat(spam);
    if (coinsWithoutPrices.length) dispatch(disableCoinsAction(coinsWithoutPrices));

    dispatch({ type: 'balance/COIN_PRICES', payload: result });
    dispatch(calculateCoinHoldingsAction());
  } finally {
    dispatch(loadingAction('coinPrices', { isLoading: false }));
  }
};

export const fetchBalances = () => async (dispatch: Dispatch, getState: () => State) => {
  const {
    user: {
      settings: { addresses },
    },
    auth: { userId, authToken },
  } = getState();

  if (userId && authToken) {
    dispatch(loadingAction('balances', { isLoading: true }));
    dispatch(loadingAction('ethereumBalances', { isLoading: true }));

    try {
      const result = await chunkDelay(
        addresses,
        2
      )(async (address: string) =>
        services.apiService.proxyEthplorer(userId, authToken, address).catch(() => undefined)
      );
      const addressInfo = compact(result);
      dispatch(ethereumBalancesAction(addressInfo));

      const balances = await services.apiService.getBalances(userId, authToken);
      dispatch(balancesAction(balances));

      const trackedCoins = Object.keys(getState().balance.balances);
      dispatch({ type: 'balance/TRACK_COINS', payload: trackedCoins });

      dispatch(calculateCoinHoldingsAction());
    } catch (e) {
      console.log(e);
    }

    dispatch(loadingAction('balances', { isLoading: false }));
    dispatch(loadingAction('ethereumBalances', { isLoading: false }));
  }
};

export const fetchCoinHistorical =
  (coin: string, toSymbol: PriceSymbol, mtimeRange?: TimeRange) =>
  async (dispatch: Dispatch, getState: () => State) => {
    dispatch(loadingAction('coinHistorical', { isLoading: true }));

    try {
      const timeRange = mtimeRange ?? getState().cryptoCompare.timeRange;

      // coingecko only (in USD); cryptocompare needs an api key now and answered every call with 401
      let result: Array<HistoricalPoint> = [];
      const coingeckoResult = await services.coinGeckoService.getPriceHistory(coin, timeRange);
      if (coingeckoResult) result = marketChartRangeToInterface(coingeckoResult);

      const {
        balance: { coinHistorical: state, loadingCoins },
      } = getState();

      dispatch({ type: 'balance/LOADING_COINS', payload: omit(loadingCoins, coin) });
      if (result.length === 0) dispatch(disableCoinsAction(coin));

      const existingIndex = findIndex(state, (c: { coin: string }) => c.coin === coin);
      const results = [...take(state, existingIndex), { coin, data: result }, ...drop(state, existingIndex + 1)];

      dispatch({ type: 'balance/COIN_HISTORICAL', payload: results });
    } finally {
      dispatch(loadingAction('coinHistorical', { isLoading: false }));
    }
  };

const coinPriority = (toSymbol: PriceSymbol, coinHoldings: CoinHoldings) => (coin: string) => {
  let ret = Infinity;
  if (coin in coinHoldings) {
    const {
      [coin]: { [toSymbol]: value },
    } = coinHoldings;
    if (value != null) {
      ret = -value;
    }
  }
  return ret;
};

export const fetchAllCoinsHistorical =
  (coinsIn: Array<string>, toSymbol: PriceSymbol, mtimeRange?: TimeRange) =>
  async (dispatch: Dispatch, getState: () => State) => {
    dispatch(loadingAction('allCoinsHistorical', { isLoading: true }));

    const {
      balance: { coinHoldings, loadingCoins, coinDataOrigin },
      cryptoCompare,
    } = getState();

    const cryptoCompareCoinsIn = coinsIn.filter(coin => coinDataOrigin[coin] == 'cryptoCompare');
    const coingeckoCoinsIn = coinsIn.filter(coin => coinDataOrigin[coin] == 'coingecko');

    const timeRange = mtimeRange ?? cryptoCompare.timeRange;
    const cryptoCompareCoins = sortBy(cryptoCompareCoinsIn, coinPriority(toSymbol, coinHoldings));
    const coingeckoCoins = sortBy(coingeckoCoinsIn, coinPriority(toSymbol, coinHoldings));

    const newLoadingCoins: Record<string, true> = fromPairs(
      map([...cryptoCompareCoins, ...coingeckoCoins], c => [c, true])
    );
    dispatch({ type: 'balance/LOADING_COINS', payload: { ...loadingCoins, ...newLoadingCoins } });

    await chunkDelay(cryptoCompareCoins, 5)(coin => dispatch(fetchCoinHistorical(coin, toSymbol, timeRange)));
    await chunkDelay(coingeckoCoins, 5)(coin => dispatch(fetchCoinHistorical(coin, toSymbol, timeRange)));

    dispatch(loadingAction('allCoinsHistorical', { isLoading: false }));
  };

export const fetchPricesForOrders = () => async (dispatch: Dispatch, getState: () => State) => {
  const {
    api: { orders },
  } = getState();
  const coins = uniq(flatMap(orders, order => [order.from, order.to]).concat(['BTC', 'ETH']));
  await dispatch(fetchCoinPrices(coins));
};
